import React, { useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useSelector } from 'react-redux';

import Modal from '../../../Modal2/Modal';
import Button from '../../../button/Button';
import { Paragraph } from '../../../typography';

import useChatDispatchActions from '../../../../customHooks/useChatDispatchActions';
import { REMOVE_INTERNAL_CHAT_GROUP } from '../../../../helpers/internalChatConstants';
import { messageGroupsService } from '../../../../newServices/corporateChat';
import { getApiErrorMessage } from '../../../../utils/internalChatGroups';

import internalChatToast from '../../utils/internalChatToast';
import useApplyGroupUpdate from '../../utils/useApplyGroupUpdate';

/** Remover outro participante ou sair do grupo (remover a si mesmo). */
const RemoveGroupParticipantModal = ({ show, group, participant, onCancel, onRemoved }) => {
    const intl = useIntl();
    const dispatchChatActions = useChatDispatchActions();
    const applyGroupUpdate = useApplyGroupUpdate();
    const loggedUserId = useSelector(state => state.auth.userData?.id);

    const [removing, setRemoving] = useState(false);

    const isLoggedUser = participant?.userId === loggedUserId;
    const groupName = <b>{group?.name}</b>;

    const handleCancel = () => {
        if (!removing) onCancel();
    }

    /** Ao sair, o grupo some da lista (e a janela dele fecha). Ao remover outra pessoa,
     *  a resposta já vem sem ela.
     */
    const handleConfirm = async () => {
        setRemoving(true);
        try {
            const res = await messageGroupsService.deleteMessageGroupParticipant(group.id, participant.userId);

            internalChatToast.success(isLoggedUser ?
                intl.formatMessage({ id: "Você saiu do grupo." }) :
                intl.formatMessage({ id: "Participante removido do grupo." }));

            setRemoving(false);
            onRemoved(isLoggedUser);

            if (isLoggedUser) {
                dispatchChatActions(REMOVE_INTERNAL_CHAT_GROUP, group.id);
            } else {
                applyGroupUpdate(res && res.data);
            }
        } catch (error) {
            internalChatToast.error(
                getApiErrorMessage(error) || intl.formatMessage({ id: "Não foi possível remover o participante. Tente novamente." })
            );
            setRemoving(false);
        }
    }

    return (
        <Modal
            show={show}
            toggle={handleCancel}
            position="center"
            size="sm"
            onPressEnter={() => {}}
        >
            <Modal.Header>
                {isLoggedUser ?
                    intl.formatMessage({ id: "Sair do grupo" }) :
                    intl.formatMessage({ id: "Remover participante" })}
            </Modal.Header>
            <Modal.Content>
                <div className="p-1">
                    <Paragraph color="gray" light>
                        {isLoggedUser ?
                            <FormattedMessage
                                id="Ao sair do grupo {group}, você deixará de ter acesso a ele e às mensagens. Confirma a saída?"
                                values={{ group: groupName }} /> :
                            <FormattedMessage
                                id="{name} deixará de ter acesso ao grupo {group} e às mensagens. Confirma a remoção?"
                                values={{ name: <b>{participant?.userName}</b>, group: groupName }} />
                        }
                    </Paragraph>
                </div>
            </Modal.Content>
            <Modal.Footer>
                <div className="d-flex justify-content-between">
                    <Button.Cancel
                        disabled={removing}
                        onClick={handleCancel} />
                    <Button.Danger
                        disabled={removing}
                        loading={removing}
                        onClick={handleConfirm}>
                        {isLoggedUser ?
                            intl.formatMessage({ id: "Sair" }) :
                            intl.formatMessage({ id: "Remover" })}
                    </Button.Danger>
                </div>
            </Modal.Footer>
        </Modal>
    );
};

export default RemoveGroupParticipantModal;
